import { TEST_HUB_URL } from '@/constants/constants';
import { apiFetch } from '@/utils/apiWrapper';

const pricingConfigurationsUrl = `${TEST_HUB_URL}pricing-configurations/`;

export type PricingValues = {
  variable: number;
  fixed: number;
  amex: number;
};

export type PricingConfigurationValues = Record<string, PricingValues>;

export type PricingConfiguration = {
  _id: string;
  organizationId: string;
  planId?: string;
  values: PricingConfigurationValues;
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

export type CreatePricingConfigurationPayload = {
  values: PricingConfigurationValues;
};

export const upsertOrganizationPricingConfiguration = async (params: {
  organizationId: string;
  payload: CreatePricingConfigurationPayload;
}): Promise<PricingConfiguration> => {
  const { organizationId, payload } = params;
  const url = `${pricingConfigurationsUrl}${organizationId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response) throw new Error('Failed to upsert organization pricing configuration');
  return response.json();
};

export const upsertPlanPricingConfiguration = async (params: {
  organizationId: string;
  planId: string;
  payload: CreatePricingConfigurationPayload;
}): Promise<PricingConfiguration> => {
  const { organizationId, planId, payload } = params;
  const url = `${pricingConfigurationsUrl}${organizationId}/${planId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response) throw new Error('Failed to upsert plan pricing configuration');
  return response.json();
};

export const getPricingConfiguration = async (params: {
  organizationId: string;
  planId?: string;
}): Promise<PricingConfiguration | null> => {
  const { organizationId, planId } = params;
  const query = new URLSearchParams();
  if (planId) query.append('planId', planId);
  const qs = query.toString();
  const url = `${pricingConfigurationsUrl}${organizationId}${qs ? `?${qs}` : ''}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch pricing configuration');
  const data = (await response.json()) as PricingConfiguration[] | PricingConfiguration;
  if (Array.isArray(data)) return data[0] ?? null;
  return data;
};

export const getDefaultPricingConfiguration = async (params: {
  organizationId: string;
}): Promise<PricingConfiguration> => {
  const { organizationId } = params;
  const url = `${pricingConfigurationsUrl}defaults/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch default pricing configuration');
  return response.json();
};

export const getMinimumPricingConfiguration = async (params: {
  organizationId: string;
}): Promise<PricingConfiguration> => {
  const { organizationId } = params;
  const url = `${pricingConfigurationsUrl}minimum/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch minimum pricing configuration');
  return response.json();
};

export const upsertMinimumPricingConfiguration = async (params: {
  organizationId: string;
  payload: CreatePricingConfigurationPayload;
}): Promise<PricingConfiguration> => {
  const { organizationId, payload } = params;
  const url = `${pricingConfigurationsUrl}minimum/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response) throw new Error('Failed to upsert minimum pricing configuration');
  return response.json();
};
